// Session courante côté front (GET /api/session) et gardes de routes, qui
// reprennent les redirections de l'ancien proxy Next.
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import type { SessionInfo } from "@shared/lib/auth-shared";
import { ApiError, apiFetch } from "./api";

export const SESSION_KEY = ["/api/session"];

/**
 * `data` : la session ; `null` : personne n'est connecté ; `undefined` : en
 * cours de lecture. Une absence de session en train d'être relue (juste après
 * une connexion) compte comme « en cours » : sinon les gardes renverraient
 * vers la connexion avant que la nouvelle session n'arrive.
 */
function useSessionQuery() {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: async () => {
      try {
        return await apiFetch<SessionInfo>("/api/session");
      } catch (cause) {
        if (cause instanceof ApiError && cause.status === 401) return null;
        throw cause;
      }
    },
    staleTime: 60_000,
  });
}

export function useSession() {
  const query = useSessionQuery();
  if (query.data === null && query.isFetching) return undefined;
  return query.data;
}

/**
 * Pourquoi la session n'a pas pu être lue (API injoignable, redémarrage) : la
 * session reste alors inconnue, et l'écran d'attente peut le dire.
 */
export function useSessionError() {
  return useSessionQuery().error;
}

export function loginPath(location: { pathname: string; search: string }) {
  return `/auth/login?next=${encodeURIComponent(`${location.pathname}${location.search}`)}`;
}

type Rule = (session: SessionInfo | null, location: ReturnType<typeof useLocation>) => string | null;

/**
 * Rend `children` si la règle ne renvoie aucune redirection, et `fallback`
 * tant que la session est inconnue (lecture en cours ou en échec). Les deux
 * sont rendus à la même place : un `fallback` du même composant que
 * `children` passe de l'un à l'autre sans être démonté.
 */
export function Guard({ rule, children, fallback = null }: { rule: Rule; children: ReactNode; fallback?: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  if (session === undefined) return <>{fallback}</>;
  const target = rule(session, location);
  return target ? <Navigate replace to={target} /> : <>{children}</>;
}
